import { useEffect, useRef, useCallback } from 'react';
import { reportTechLocation } from '../services/api';

interface UseLocationTrackingOptions {
  resourceId: number | null;
  resourceName?: string;
  enabled: boolean;
  intervalMs?: number; // Default: 5 minutes
  workHoursStart?: number; // Default: 7
  workHoursEnd?: number; // Default: 18
}

export function useLocationTracking({
  resourceId,
  resourceName,
  enabled,
  intervalMs = 5 * 60 * 1000,
  workHoursStart = 7,
  workHoursEnd = 18,
}: UseLocationTrackingOptions) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isWithinWorkHours = useCallback(() => {
    // Use Pacific Time to match the server-side push side (api-timers/.../pushService.ts).
    // Browser local time would drift apart from server filtering for techs in other zones.
    const now = new Date();
    const pacific = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const hour = pacific.getHours();
    const day = pacific.getDay();
    // Skip weekends (0=Sun, 6=Sat)
    if (day === 0 || day === 6) return false;
    return hour >= workHoursStart && hour < workHoursEnd;
  }, [workHoursStart, workHoursEnd]);

  const sendLocation = useCallback(async () => {
    if (!resourceId || !enabled || !isWithinWorkHours()) return;

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false, // Low accuracy to save battery
          timeout: 10000,
          maximumAge: 60000, // Cache for 1 min
        });
      });

      await reportTechLocation({
        resourceId,
        resourceName: resourceName || '',
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      });
    } catch (err) {
      // Silently fail - location is best-effort
      console.warn('Location report failed:', err);
    }
  }, [resourceId, resourceName, enabled, isWithinWorkHours]);

  useEffect(() => {
    if (!enabled || !resourceId) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Send immediately on enable
    sendLocation();

    // Then send periodically
    intervalRef.current = setInterval(sendLocation, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, resourceId, intervalMs, sendLocation]);
}
